import { OrderSide } from '@katanaperps/katana-perps-sdk';
import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import SpinningRoundedBorder from 'components/basic/animations/SpinningRoundedBorder';
import SuccesAnimation from 'components/basic/animations/SuccessAnimation';
import Button from 'components/basic/Button/Button';
import ButtonRouterLink from 'components/basic/Button/ButtonRouterLink';
import { staticDispatch, useSelector } from 'reduxState/store';
import { showWalletModal } from 'reduxState/wallet/walletDetailsReducer';

import { PanelState } from './buyPanelReducer';

const Wrapper = styled.div`
  display: flex;
  position: relative;
  gap: 8px;
`;

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 123;
  opacity: 0;
  transition: opacity 0.4s ease;
  &:hover {
    opacity: 1;
  }
`;

const ActionWrap = styled.div`
  flex: 1;
  position: relative;
`;

const OverlayButtonWrap = styled.div<{ $active: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  /* Make button appear with better color transition */
  background: ${({ $active }) =>
    $active
      ? 'rgb(from var(--elevation-light) r g b / 0.7)'
      : 'rgb(from var(--elevation-light) r g b / 0.3)'};
  z-index: 123;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.7s ease;
`;

const TextWithTransition = styled.div<{ $hidden: boolean }>`
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity 0.8s ease;
`;

function OverlayButton({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActive(true);
  }, []);
  return <OverlayButtonWrap $active={active}>{children}</OverlayButtonWrap>;
}

function ActionButton({
  state,
  side,
  onPlace,
  disabled,
  children,
}: {
  state: PanelState['state'];
  side: 'buy' | 'sell';
  onPlace: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  const isLoading =
    (side === 'sell' && state === 'loadingSell') ||
    (side === 'buy' && state === 'loadingBuy');
  const isSuccess =
    (side === 'sell' && state === 'successSell') ||
    (side === 'buy' && state === 'successBuy');

  /** We only show loader after delay to prevent disctraction and slow feel */
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    let timeoutId: number;
    if (isLoading) {
      timeoutId = window.setTimeout(() => setShowLoader(true), 1300);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowLoader(false);
    }
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  return (
    <ActionWrap>
      {isLoading || isSuccess ? (
        <OverlayButton>
          {
            isSuccess ? (
              <SuccesAnimation size={35} />
            ) : showLoader ? (
              <SpinningRoundedBorder size={30} />
            ) : null /* Loader delay */
          }
        </OverlayButton>
      ) : null}
      <Button
        onClick={onPlace}
        disabled={disabled}
        color={side === 'buy' ? 'successWithDarkText' : 'red'}
      >
        <TextWithTransition $hidden={showLoader || isSuccess}>
          {children}
        </TextWithTransition>
      </Button>
    </ActionWrap>
  );
}

type Props = {
  onPlaceOrder: (type: OrderSide) => void;
  buyEnabled: boolean;
  sellEnabled: boolean;
  panelState: PanelState['state'];
  singleAction?: 'buy' | 'sell';
};

function BuySellPanelButton({
  onPlaceOrder,
  buyEnabled,
  sellEnabled,
  panelState,
  singleAction,
}: Props) {
  // const isMobile = useSelector(state => state.screen.isMobile);
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const isRestrictedRegion = !!useSelector(
    state => state.region.restrictedRegion
  );
  return (
    <Wrapper>
      {!loggedIn ? (
        <Overlay>
          <ButtonRouterLink to="/wallet/api-keys/login">Login</ButtonRouterLink>
        </Overlay>
      ) : !walletAddress ? (
        <Overlay>
          <Button onClick={() => staticDispatch(showWalletModal())}>
            Connect Wallet
          </Button>
        </Overlay>
      ) : null}

      {(!singleAction || singleAction === 'buy') && (
        <ActionButton
          disabled={isRestrictedRegion || !buyEnabled}
          onPlace={() => onPlaceOrder('buy')}
          side="buy"
          state={panelState}
        >
          Buy / Long
        </ActionButton>
      )}
      {(!singleAction || singleAction === 'sell') && (
        <ActionButton
          disabled={isRestrictedRegion || !sellEnabled}
          onPlace={() => onPlaceOrder('sell')}
          side="sell"
          state={panelState}
        >
          Sell / Short
        </ActionButton>
      )}
    </Wrapper>
  );
}

export default BuySellPanelButton;
